import { getDefaultClient } from '../../../client/defaultClient';
import { createVisit } from '../../auth/createVisit';
import { emitWalletAccountsChangedEvent } from '../emitWalletAccountsChangedEvent';
import { getWalletAccounts } from '../getWalletAccounts';
import { getWalletProviderByKey } from '../getWalletProviderByKey';
import type { HardwareWalletVendor } from '../hardwareWallet/hardwareWallet.types';
import { setUnverifiedWalletAccounts } from '../unverifiedWalletAccounts/setUnverifiedWalletAccounts';
import { assertWalletProviderMethodDefined } from '../utils/assertWalletProviderMethodDefined';
import { formatWalletAccountId } from '../utils/formatWalletAccountId';
import { getAddressesWithTypesFromConnectionResult } from '../utils/getAddressesWithTypesFromConnectionResult';
import type { WalletAccount } from '../walletAccount';
import { NoAddressFoundError } from './../../../errors/NoAddressFoundError';

type ConnectWithWalletProviderParams = {
  addToDynamicWalletAccounts?: boolean;
  hardwareWalletVendor?: HardwareWalletVendor;
  walletProviderKey: string;
};

/**
 * Connects to a wallet provider and returns the connected wallet account.
 *
 * Note: This function will not verify the wallet account, use the verifyWalletAccount function to do that
 * or connectAndVerifyWithWalletProvider to do that in a single step.
 * @see verifyWalletAccount
 * @see connectAndVerifyWithWalletProvider
 *
 * @param params.walletProviderKey - The unique key identifying the wallet provider to connect to (e.g. 'metamaskevm', 'phantomsol').
 * @param [params.addToDynamicWalletAccounts] - Whether to add the connected wallet account to the user's wallet accounts. Defaults to true.
 * @param [params.hardwareWalletVendor] - The hardware wallet vendor, if connecting via a hardware wallet (e.g. 'ledger').
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the connected wallet account.
 * @throws NoAddressFoundError If the request to connect was successful but no address is connected to your app.
 */
export const connectWithWalletProvider = async (
  {
    walletProviderKey,
    addToDynamicWalletAccounts = true,
    hardwareWalletVendor,
  }: ConnectWithWalletProviderParams,
  client = getDefaultClient()
): Promise<WalletAccount> => {
  const walletProvider = getWalletProviderByKey(
    {
      walletProviderKey,
    },
    client
  );

  assertWalletProviderMethodDefined(walletProvider, 'connect');

  const { addresses } = await walletProvider.connect();

  const currentAddresses = getWalletAccounts(client).map(
    ({ address }) => address
  );

  const mainAddress = addresses?.length ? addresses[0].address : null;

  /**
   * This error is thrown in case the wallet has no addresses connected
   * to the dapp. Then the error is thrown so the consumer can instruct the
   * user on the next steps.
   */
  if (!mainAddress) {
    throw new NoAddressFoundError();
  }

  const walletAccount: WalletAccount = {
    address: mainAddress,
    addressesWithTypes: getAddressesWithTypesFromConnectionResult({
      addresses,
    }),
    chain: walletProvider.chain,
    hardwareWalletVendor,
    id: formatWalletAccountId({
      address: mainAddress,
      chain: walletProvider.chain,
      walletProviderKey,
    }),
    lastSelectedAt: null,
    verifiedCredentialId: null,
    walletProviderKey,
  };

  if (currentAddresses.includes(mainAddress)) {
    return walletAccount;
  }

  if (addToDynamicWalletAccounts) {
    setUnverifiedWalletAccounts(
      { unverifiedWalletAccountsToUpdate: [walletAccount] },
      client
    );

    emitWalletAccountsChangedEvent(client);

    /*
     * send information to backend to kick off background jobs
     * this is an async work, but does not need to be awaited
     */
    void createVisit({ walletAccount }, client);
  }

  return walletAccount;
};
